import { Controller } from "stimulus";
import { Canvas, Image, Rect, config, filters } from "fabric";

const LOGICAL_WIDTH = 400;
const LOGICAL_HEIGHT = 300;
const MAX_EXPORT_SIDE = 2048;
// Taller than any charm the canvas can show, so the clip only limits width and top.
const STRIP_CLIP_HEIGHT = 4000;
const EDGE_TOLERANCE = 0.5;

// Browsers default to low-quality image smoothing. A large charm scaled on the
// editor canvas then looks soft while it is resized.
function paintSharply(image) {
    image.set({
        objectCaching: false,
        noScaleCache: false,
        imageSmoothing: true,
    });

    const renderFill = image._renderFill.bind(image);
    image._renderFill = (ctx) => {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        renderFill(ctx);
    };
}

// Opaque pixels per row, so the editor can tell which part of the charm
// sticks out past the link without scanning the image on every drag.
// When the pixels cannot be read, every row counts as fully opaque.
function scanOpaque(element) {
    const width = element.naturalWidth || element.width;
    const height = element.naturalHeight || element.height;
    if (!width || !height) {
        return null;
    }

    const solid = { width, height, rows: new Array(height).fill({ minX: 0, maxX: width - 1 }) };
    const sample = document.createElement("canvas");
    sample.width = width;
    sample.height = height;
    const ctx = sample.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
        return solid;
    }
    ctx.drawImage(element, 0, 0, width, height);

    let data;
    try {
        data = ctx.getImageData(0, 0, width, height).data;
    } catch (e) {
        return solid;
    }

    const rows = new Array(height);
    for (let y = 0; y < height; y++) {
        let minX = width;
        let maxX = -1;
        const offset = y * width * 4;
        for (let x = 0; x < width; x++) {
            if (data[offset + x * 4 + 3] < 8) {
                continue;
            }
            if (x < minX) {
                minX = x;
            }
            if (x > maxX) {
                maxX = x;
            }
        }
        rows[y] = maxX >= 0 ? { minX, maxX } : null;
    }

    return { width, height, rows };
}

export default class extends Controller {
    static targets = ["canvas","result","message", "left", "top", "width", "height", "italianPreview", "italianSlot", "italianOffset"]

    connect() {

        this.baseImage = this.data.get("base-value");
        this.overlayImage = this.data.get("overlay-value");
        // Keep at least a 2x backing store so scaling samples more source pixels
        // than the 400px preview, including on screens without a high pixel ratio.
        this.pixelRatio = Math.max(window.devicePixelRatio || 1, 2);
        config.devicePixelRatio = this.pixelRatio;

        this.canvas = new Canvas(this.canvasTarget, {
            width: LOGICAL_WIDTH,
            height: LOGICAL_HEIGHT,
            enableRetinaScaling: true,
            imageSmoothingEnabled: true,
        });
        this.canvas.on("before:render", ({ ctx }) => {
            if (!ctx) {
                return;
            }
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
        });
        this.overlay = null;
        this.base = null;
        this.stripMark = null;
        this.opaque = null;
        this.exporting = false;
        this.keepClip = new Rect({
            originX: "left",
            originY: "top",
            absolutePositioned: true,
            inverted: true,
            fill: "black",
            strokeWidth: 0,
        });
        this.italianOffsetX = 0;
        this.italianOffsetY = 0;
        this.canvas.on("object:moving", () => this.updateInputs());
        this.canvas.on("object:scaling", () => this.updateInputs());
        this.canvas.on("after:render", (opt) => this.paintStripGuides(opt));

        this.loadBase();
        this.loadOverlay();
    }

    loadBase() {
        Image.fromURL(this.baseImage,{}).then((img) => {
            paintSharply(img);
            img.selectable = false;
            img.evented = false;
            img.set({
                originX: "center",
                originY: "center",
                left: this.canvas.width / 2,
                top: this.canvas.height / 2,
            });

            this.base = img;
            this.canvas.add(img);
            this.syncStripMark();
            this.canvas.requestRenderAll();

        });
    }

    loadOverlay() {
        Image.fromURL(this.overlayImage,{}).then((img) => {
            paintSharply(img);
            // The face file keeps Nomination's pixel scale and the base is shown at
            // its real 127px, so the default scale of 1 is the Italian size.
            img.set({
                originX: "center",
                originY: "center",
                left: this.canvas.width / 2,
                top: this.canvas.height / 2,
                cornerStyle: "circle",
                transparentCorners: false,
            });
            // Corner handles scale both sides together; the side handles would stretch the charm.
            img.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false });

            this.overlay = img;
            this.opaque = scanOpaque(img.getElement());
            this.stripMark = this.createStripMark(img.getElement());
            this.canvas.add(img, this.stripMark);
            this.canvas.setActiveObject(img);
            this.updateInputs();
        });
    }

    // A red copy of the charm, clipped to what lies outside the link. It follows
    // the overlay and is left out of the saved picture.
    createStripMark(element) {
        const mark = new Image(element, {
            originX: "center",
            originY: "center",
            selectable: false,
            evented: false,
            objectCaching: false,
            clipPath: this.keepClip,
            filters: [new filters.BlendColor({ color: "#be1e1e", mode: "tint", alpha: 0.7 })],
        });
        paintSharply(mark);
        mark.isStripMark = true;

        return mark;
    }

    updateInputs() {
        if (!this.overlay) return;

        this.leftTarget.value = Math.round(this.overlay.left);
        this.topTarget.value = Math.round(this.overlay.top);
        this.widthTarget.value = Math.round(this.overlay.getScaledWidth());
        this.heightTarget.value = Math.round(this.overlay.getScaledHeight());
        this.syncStripMark();
    }

    updateOverlay(event) {
        if (!this.overlay) return;

        const ratio = this.overlay.height / this.overlay.width;
        const field = event && event.target;
        let width = parseFloat(this.widthTarget.value);
        let height = parseFloat(this.heightTarget.value);

        // Width and height follow each other, so the charm keeps its proportions.
        if (field === this.heightTarget) {
            width = height / ratio;
        } else {
            height = width * ratio;
        }
        if (!(width > 0) || !(height > 0)) return;

        this.overlay.set({
            left: parseFloat(this.leftTarget.value) || 0,
            top: parseFloat(this.topTarget.value) || 0,
            scaleX: width / this.overlay.width,
            scaleY: height / this.overlay.height
        });
        this.overlay.setCoords();
        this.canvas.requestRenderAll();

        if (field === this.heightTarget) {
            this.widthTarget.value = Math.round(width);
        } else if (field === this.widthTarget) {
            this.heightTarget.value = Math.round(height);
        }
    }

    changeBase(event) {
        const url = event.target.dataset.baseUrl;
        if (!url) return;

        this.baseImage = url;
        this.base = null;
        this.overlay = null;
        this.stripMark = null;
        this.opaque = null;
        this.canvas.clear();
        this.loadBase();
        this.loadOverlay();
    }

    generatePreviewImage(){
        const base = this.baseRect();
        if (!base) return;

        const left = base.left;
        const top = base.top;
        const width = base.width;
        const height = Math.max(1, this.columnBottom() - top);

        this.exporting = true;
        const dataURL = this.canvas.toDataURL({
            format: "png",
            multiplier: this.exportMultiplier(width, height),
            left,
            top,
            width,
            height,
            filter: (object) => !object.isStripMark,
        });
        this.exporting = false;

        return dataURL;
    }

    baseRect() {
        if (!this.base) {
            return null;
        }
        const width = this.base.getScaledWidth();
        const height = this.base.getScaledHeight();
        const left = this.base.left - width / 2;
        const top = this.base.top - height / 2;

        return { left, top, width, height, right: left + width, bottom: top + height };
    }

    // Opaque rows of the charm, projected onto the canvas.
    overlayRows() {
        const img = this.overlay;
        if (!img || !this.opaque) {
            return [];
        }
        const width = img.getScaledWidth();
        const height = img.getScaledHeight();
        const left = img.left - width / 2;
        const top = img.top - height / 2;
        const scaleX = width / this.opaque.width;
        const scaleY = height / this.opaque.height;
        const rows = [];

        this.opaque.rows.forEach((row, y) => {
            if (!row) {
                return;
            }
            rows.push({
                top: top + y * scaleY,
                bottom: top + (y + 1) * scaleY,
                left: left + row.minX * scaleX,
                right: left + (row.maxX + 1) * scaleX,
            });
        });

        return rows;
    }

    // The saved picture starts at the top of the link and keeps anything
    // hanging below it, as long as that part stays inside the link width.
    columnBottom() {
        const base = this.baseRect();
        if (!base) {
            return 0;
        }
        let bottom = base.bottom;
        this.overlayRows().forEach((row) => {
            if (row.bottom > bottom && row.right > base.left && row.left < base.right) {
                bottom = row.bottom;
            }
        });

        return bottom;
    }

    hasStripOverflow() {
        const base = this.baseRect();
        if (!base) {
            return false;
        }

        return this.overlayRows().some((row) =>
            row.left < base.left - EDGE_TOLERANCE
            || row.right > base.right + EDGE_TOLERANCE
            || (row.top < base.top - EDGE_TOLERANCE && row.right > base.left && row.left < base.right)
        );
    }

    syncStripMark() {
        if (!this.stripMark || !this.overlay) {
            return;
        }
        const base = this.baseRect();
        this.stripMark.set({
            left: this.overlay.left,
            top: this.overlay.top,
            scaleX: this.overlay.scaleX,
            scaleY: this.overlay.scaleY,
            visible: base !== null,
            dirty: true,
        });
        if (base) {
            this.keepClip.set({
                left: base.left,
                top: base.top,
                width: base.width,
                height: STRIP_CLIP_HEIGHT,
            });
        }
        this.stripMark.setCoords();
    }

    paintStripGuides({ ctx }) {
        if (this.exporting || !ctx || !this.hasStripOverflow()) {
            return;
        }
        const base = this.baseRect();
        const bottom = this.columnBottom();
        ctx.save();
        ctx.strokeStyle = "rgba(190, 30, 30, 0.95)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(base.left + 0.5, base.top + 0.5);
        ctx.lineTo(base.right - 0.5, base.top + 0.5);
        ctx.moveTo(base.left + 0.5, base.top);
        ctx.lineTo(base.left + 0.5, bottom);
        ctx.moveTo(base.right - 0.5, base.top);
        ctx.lineTo(base.right - 0.5, bottom);
        ctx.stroke();
        ctx.restore();
    }

    exportMultiplier(width, height) {
        const scale = this.overlay
            ? Math.min(Math.abs(this.overlay.scaleX) || 1, Math.abs(this.overlay.scaleY) || 1)
            : 1;
        // Draw the charm back toward its original pixel size instead of the 400px canvas.
        let multiplier = Math.max(this.pixelRatio, 1 / scale);
        const longest = Math.max(width, height, 1);

        if (longest * multiplier > MAX_EXPORT_SIDE) {
            multiplier = MAX_EXPORT_SIDE / longest;
        }

        return multiplier;
    }

    save() {
        const dataURL = this.generatePreviewImage();
        if (!dataURL) return;

        this.postImage({ image: dataURL })
            .then(() => (this.messageTarget.style.display = "block"))
            .catch(() => window.alert("Снимката не можа да се запише."));
    }

    useSource(event) {
        const button = event.currentTarget;
        const originalText = button.textContent;
        const status = this.useStatus(button);

        button.disabled = true;
        status.className = "small mt-2 text-muted";
        status.textContent = "Записва се...";

        this.postImage({
            source: "italian",
            offsetX: this.italianOffsetX || 0,
            offsetY: this.italianOffsetY || 0
        })
            .then(() => {
                button.textContent = "Записано";
                status.className = "small mt-2 text-success";
                status.textContent = "Снимката е записана.";
                this.messageTarget.style.display = "block";
            })
            .catch(() => {
                button.disabled = false;
                button.textContent = originalText;
                status.className = "small mt-2 text-danger";
                status.textContent = "Снимката не можа да се вземе от Nomination.";
            });
    }

    nudgeItalian(event) {
        const stepX = parseInt(event.currentTarget.dataset.nudgeX || "0", 10);
        const stepY = parseInt(event.currentTarget.dataset.nudgeY || "0", 10);
        this.italianOffsetX = (this.italianOffsetX || 0) + stepX;
        this.italianOffsetY = (this.italianOffsetY || 0) + stepY;
        this.renderItalianOffset("Обновява се...");

        clearTimeout(this.italianRefreshTimer);
        this.italianRefreshTimer = setTimeout(() => this.refreshItalianPreviews(), 200);
    }

    renderItalianOffset(suffix) {
        if (!this.hasItalianOffsetTarget) {
            return;
        }
        const x = this.italianOffsetX || 0;
        const y = this.italianOffsetY || 0;
        const position = x === 0 && y === 0
            ? "Без отместване"
            : "Отместване: " + (x > 0 ? "надясно " + x : x < 0 ? "наляво " + Math.abs(x) : "0")
                + ", " + (y > 0 ? "надолу " + y : y < 0 ? "нагоре " + Math.abs(y) : "0");
        this.italianOffsetTarget.textContent = suffix ? position + " · " + suffix : position;
    }

    refreshItalianPreviews() {
        if (!this.hasItalianPreviewTarget || !this.hasItalianSlotTarget) {
            return;
        }
        const generation = (this.italianGeneration || 0) + 1;
        this.italianGeneration = generation;
        const x = this.italianOffsetX || 0;
        const y = this.italianOffsetY || 0;
        let pending = 2;
        let failed = false;

        [this.italianPreviewTarget, this.italianSlotTarget].forEach((img) => {
            const url = new URL(img.src, window.location.href);
            url.searchParams.set("offsetX", String(x));
            url.searchParams.set("offsetY", String(y));
            url.searchParams.delete("t");
            const next = url.pathname + url.search;
            // fabric's Image import shadows the browser constructor.
            const loader = document.createElement("img");
            const finish = (ok) => {
                if (this.italianGeneration !== generation) {
                    return;
                }
                if (ok) {
                    img.src = next;
                } else {
                    failed = true;
                }
                pending -= 1;
                if (pending === 0) {
                    this.renderItalianOffset(failed ? "Снимката не се обнови" : "");
                }
            };
            loader.onload = () => finish(true);
            loader.onerror = () => finish(false);
            loader.src = next;
        });
    }

    postImage(body) {
        return fetch(window.location.href, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body)
        }).then((response) => {
            if (!response.ok) {
                throw new Error(response.statusText);
            }
        });
    }

    useStatus(button) {
        let status = button.parentElement.querySelector("[data-use-status]");
        if (!status) {
            status = document.createElement("div");
            status.setAttribute("data-use-status", "");
            button.insertAdjacentElement("afterend", status);
        }

        return status;
    }
}